/* ═══════════════════════════════════════════════════════════════════════
   The dashboard — what an account opens onto.

   Answers four questions, in the order somebody actually has them:

     1. is anything waiting on me?
     2. what's happened since I last looked?
     3. how much credit is left?
     4. where are my pages?

   ── The empty state is a welcome, not a form ───────────────────────────
   It used to be the "make a page" form, sitting there demanding a trading
   name from somebody who had signed up ninety seconds earlier and wanted
   to look around. A screen that opens with a form is a screen that has
   decided what you came for. So: a short hello, what happens next in one
   line, and a button.

   ── Two doors, and one of them is louder ──────────────────────────────
   The button is the interview, and it is the loud one: crAIg asks, and
   watching him ask well is the best argument this product has. Under it,
   in small type, is the questionnaire — every question a button, folded
   away until somebody asks for it. See TickBoxesInstead and Questionnaire
   for why that form is allowed to exist and what it is not allowed to
   become.

   ── Account-wide, because they have one morning ────────────────────────
   The inbox and the money are merged across every page. A person with
   three businesses does not want three inboxes; they want everything that
   happened, in order, with the business named on each line. Per-page
   versions of both still exist inside each page.
   ═══════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useState } from "react";
import { accentFor, applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { CraigFace } from "./components/CraigFace";
import { DashboardLink } from "./components/chrome";
import { LetCraigDoIt, Wordmark } from "./components/Brand";
import { PasswordField } from "./components/PasswordField";

interface Page {
  id: string;
  slug: string;
  displayName: string;
  industry: string;
  branding: { accent?: string; scheme?: string; logoUrl?: string };
  published: boolean;
  /* What this chat has SPENT this month. Not what it has left — there is
     one balance and it belongs to the account. A figure per chat here is
     how somebody comes to believe they have three separate purses, which
     is exactly the belief that made deleting one feel like losing money. */
  spentThisMonthMicros: number;
  conversationsThisMonth: number;
  waitingOnYou: number;
  inProgress: number;
  link: string;
}

interface InboxItem {
  id: string;
  page: { slug: string; displayName: string };
  title: string;
  body: string | null;
  delivered: boolean;
  at: string;
  read: boolean;
  open: string | null;
}

interface HomeData {
  email: { address: string; verified: boolean };
  pages: Page[];
  /* The five things a chat can be set to handle, and the worked starting
     points. Both are for the questionnaire — the quiet second door — and
     both come from the server rather than being restated here, so what a
     business can tick is exactly what crAIg asks about. */
  purposes: { id: string; label: string; hint: string }[];
  templates: { id: string; label: string; blurb: string }[];
  credit: {
    balanceMicros: number;
    spentThisMonthMicros: number;
    toppedUpThisMonthMicros: number;
    conversationsThisMonth: number;
    perCaseMicros: number;
  };
  waitingOnYou: number;
  inbox: { unread: number; items: InboxItem[] };
}

const money = (micros: number) => `$${(micros / 1_000_000).toFixed(2)}`;

/** A trading name, as a web address. Never shown as a question. */
const suggest = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 32);

export function Home() {
  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [data, setData] = useState<HomeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applyTheme(theme);
    applyAccent(undefined, theme);
  }, [theme]);

  const load = useCallback(() => {
    fetch("/api/dash/home")
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch((s) => {
        if (s === 401) window.location.href = "/account/sign-in";
        else setError("Couldn't load that just now.");
      });
  }, []);

  useEffect(load, [load]);

  if (error) return <Centre>{error}</Centre>;
  if (!data) return <Centre>Loading…</Centre>;

  const first = data.pages.length === 0;

  return (
    <div className="min-h-screen flex flex-col">
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          {/* Yes, on the dashboard itself, pointing at the dashboard.
              Asked for deliberately: a button that is on five screens and
              missing on the sixth is a button people stop trusting, and
              the one place it is allowed to do nothing is the place you
              were already trying to get to. */}
          <div className="flex items-center gap-2 min-w-0">
            <DashboardLink />
            <CraigFace size={24} />
            <Wordmark size={15} />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="hidden sm:block truncate"
              style={{ fontSize: 12.5, color: "var(--ink-3)", maxWidth: 200 }}
            >
              {data.email.address}
            </span>
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              aria-label="Switch theme"
              style={{ padding: "0.3rem 0.55rem" }}
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-term"
              onClick={async () => {
                await fetch("/api/account/sign-out", { method: "POST" });
                window.location.href = "/account/sign-in";
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-6 space-y-4">
        {!data.email.verified && <Verify email={data.email.address} />}

        {/* The welcome leads on an empty account, because making the first
            chat is the only thing worth doing. The money still shows under
            it — an earlier version rendered the credit strip only once
            there were chats, so a new account could not find its balance
            at all and had no way to learn that credit exists. */}
        {first && (
          <Welcome purposes={data.purposes} templates={data.templates} onMade={load} />
        )}

        <Money credit={data.credit} waiting={data.waitingOnYou} pages={data.pages.length} />

        {!first && (
          <>
            <Inbox
              inbox={data.inbox}
              onRead={async (id) => {
                await fetch("/api/dash/inbox/read", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(id ? { id } : {}),
                });
                load();
              }}
            />

            <section>
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <div className="term-label" style={{ paddingTop: 6 }}>
                  Your chats · {data.pages.length}
                </div>
                {/* The same first door as the empty state — a second chat
                    is set up the way the first one was, by talking to him
                    — with the questionnaire tucked under it, quieter. */}
                <a href="/start" className="btn btn-quiet btn-term">
                  + New chat
                </a>
              </div>

              {/* Full width, not tucked beside the button: collapsed it is
                  a line of small type under it, but opened it is a form
                  and a form squeezed into a corner is unusable. */}
              <div className="mb-2.5">
                <TickBoxesInstead
                  align="end"
                  purposes={data.purposes}
                  templates={data.templates}
                  onMade={load}
                />
              </div>

              <div className="space-y-2.5">
                {data.pages.map((p) => (
                  <Card key={p.id} page={p} theme={theme} />
                ))}
              </div>
            </section>
          </>
        )}

        <CloseAccount email={data.email.address} pages={data.pages} />
      </main>

      <LetCraigDoIt />
    </div>
  );
}

/* ── Closing the account ────────────────────────────────────────────────
   The most destructive thing in the product, and the only one that
   removes somebody ELSE'S data as a side effect: a plumber's customers
   never agreed to anything, and their photographs go too.

   So it is folded away behind a quiet link, it names every chat that
   goes with the real number of jobs on each, and it asks for the email
   address AND the password. A live session on a borrowed laptop should
   not be enough to end a business. */

function CloseAccount({ email, pages }: { email: string; pages: Page[] }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <section className="pt-6 pb-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: "none",
            border: 0,
            padding: 0,
            font: "inherit",
            fontSize: 12.5,
            color: "var(--ink-3)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Close my account
        </button>
      </section>
    );
  }

  return (
    <section
      className="panel px-5 py-5 mt-4"
      style={{ background: "var(--warn-tint)", borderColor: "var(--danger)" }}
    >
      <div className="term-label">Close this account</div>
      <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 8, lineHeight: 1.55 }}>
        This deletes your account and{" "}
        <strong>
          {pages.length === 0
            ? "everything on it"
            : `all ${pages.length} of your chat${pages.length === 1 ? "" : "s"}`}
        </strong>
        . Every job your customers sent, every photo and document with them, and every crAIg page
        you've built. There is no undo and we keep no copy.
      </p>

      {pages.length > 0 && (
        <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none" }}>
          {pages.map((p) => (
            <li
              key={p.id}
              className="mono"
              style={{ fontSize: 12.5, color: "var(--ink-2)", padding: "2px 0" }}
            >
              letcraig.ai/{p.slug}
              {p.waitingOnYou + p.inProgress > 0 && (
                <span style={{ color: "var(--danger)" }}>
                  {" "}
                  · {p.waitingOnYou + p.inProgress} job
                  {p.waitingOnYou + p.inProgress === 1 ? "" : "s"} still open
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <label className="block mt-3" style={{ maxWidth: 380 }}>
        <span className="term-label" style={{ display: "block", marginBottom: 4 }}>
          Type <span className="mono">{email}</span> to confirm
        </span>
        <input
          className="field mono"
          autoFocus
          autoComplete="off"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>

      <div className="mt-3" style={{ maxWidth: 380 }}>
        <PasswordField
          id="close-account-password"
          label="And your password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
      </div>

      {error && (
        <p role="alert" style={{ fontSize: 13.5, color: "var(--danger)", marginTop: 8 }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className="btn btn-primary btn-term"
          disabled={busy || typed.trim().toLowerCase() !== email.toLowerCase()}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const res = await fetch("/api/account", {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirm: typed.trim(), password }),
              });
              const body = (await res.json().catch(() => ({}))) as { error?: string };
              if (!res.ok) return setError(body.error ?? "That didn't work.");
              window.location.href = "/";
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Closing…" : "Delete my account and everything on it"}
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-term"
          onClick={() => {
            setOpen(false);
            setTyped("");
            setPassword("");
            setError(null);
          }}
        >
          Keep it
        </button>
      </div>
    </section>
  );
}

/* ── The first minute ───────────────────────────────────────────────────
   Not the form. Somebody who signed up ninety seconds ago is deciding
   whether this was worth it, and a blank "trading name" box is a demand
   rather than an invitation. */

function Welcome({
  purposes,
  templates,
  onMade,
}: {
  purposes: HomeData["purposes"];
  templates: HomeData["templates"];
  onMade(): void;
}) {
  return (
    <section className="panel panel-ticks px-5 py-6">
      <div className="flex items-center gap-2.5 mb-3">
        <CraigFace size={34} />
        <h1 className="mono" style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
          You're in.
        </h1>
      </div>
      <p style={{ fontSize: 14.5, color: "var(--ink-2)", lineHeight: 1.6, maxWidth: 520 }}>
        A <strong>chat</strong> is a page your customers land on with crAIg in it — he asks what
        you'd ask and hands you a job you can start. You can have as many as you like, and nothing
        is public until you say so.
      </p>

      {/*
        The interview is the loud door, and it stays that way.

        Somebody deciding whether crAIg can hold a conversation with their
        customers learns more from three minutes of him holding one with
        them than from any amount of copy on this page — and a business
        that has just watched him ask a good question they hadn't thought
        of is a business that believes the rest of it.

        Underneath it, and deliberately smaller, is the questionnaire. Not
        everybody has three minutes, and somebody making their fourth chat
        already knows what they want. See TickBoxesInstead.
      */}
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <a href="/start" className="btn btn-primary btn-term">
          Set up my first chat
        </a>
        <span style={{ fontSize: 13, color: "var(--ink-3)" }}>
          crAIg asks; it takes a few minutes.
        </span>
      </div>

      <div className="mt-3">
        <TickBoxesInstead purposes={purposes} templates={templates} onMade={onMade} />
      </div>
    </section>
  );
}

/* ── The second door ────────────────────────────────────────────────────
   Folded away until asked for, because the interview is the one we want
   people to take. A button rather than a link so it cannot be mistaken
   for navigation: nothing loads, the questions appear underneath. */

function TickBoxesInstead({
  purposes,
  templates,
  onMade,
  align = "start",
}: {
  purposes: HomeData["purposes"];
  templates: HomeData["templates"];
  onMade(): void;
  /** Which end the collapsed line sits at. The form is always full width. */
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <div style={{ textAlign: align === "end" ? "right" : "left" }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: "none",
            border: 0,
            padding: 0,
            font: "inherit",
            fontSize: 13,
            color: "var(--ink-3)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Rather not talk? Tick a few boxes instead
        </button>
      </div>
    );
  }

  return (
    <Questionnaire
      purposes={purposes}
      templates={templates}
      onCancel={() => setOpen(false)}
      onMade={() => {
        setOpen(false);
        onMade();
      }}
    />
  );
}

/* ── Money ──────────────────────────────────────────────────────────────
   Three numbers, and the first is the one people open the page for.
   Deliberately not a chart: nobody running a trade has ever wanted one,
   and "am I about to run out" is a number, not a trend.

   ── One balance, whatever they have built ──────────────────────────────
   It is the ACCOUNT'S credit, not a chat's. That used to be the other way
   round and it produced two wrong things at once: a new chat came with
   another five free dollars, and deleting a chat took its balance with it.
   The wording here says "your credit" and never "this chat's", because
   the screen is where somebody forms the belief. */

function Money({
  credit,
  waiting,
  pages,
}: {
  credit: HomeData["credit"];
  waiting: number;
  pages: number;
}) {
  const fresh = pages === 0;
  const empty = credit.balanceMicros <= 0;
  const low = !empty && credit.balanceMicros < 2_000_000;

  return (
    <section
      className="panel panel-ticks px-5 py-5"
      style={
        empty
          ? { background: "var(--warn-tint)", borderColor: "var(--danger)" }
          : low
            ? { background: "var(--warn-tint)", borderColor: "var(--warn)" }
            : undefined
      }
    >
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
        <Stat
          label="Your credit"
          value={money(Math.max(0, credit.balanceMicros))}
          sub={pages > 1 ? `shared across ${pages} chats` : undefined}
          big
          accent={empty || low}
        />
        <Stat label="Needs you" value={String(waiting)} big accent={waiting > 0} />
        <Stat
          label="This month"
          value={`${credit.conversationsThisMonth} chat${credit.conversationsThisMonth === 1 ? "" : "s"}`}
          sub={money(credit.spentThisMonthMicros)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2.5 mt-4">
        <button
          type="button"
          className={empty || low ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
          disabled
        >
          Add credit
        </button>
        <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
          {fresh
            ? "Yours to spend — it works across every chat you make."
            : "Card payments aren't switched on yet — get in touch and we'll top you up."}
        </span>
      </div>

      {empty && (
        <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 10, lineHeight: 1.55 }}>
          <strong>Your live chats have stopped taking new enquiries.</strong> Anyone opening them is
          asked to contact you directly. Nothing is lost — they start again the moment you top up.
        </p>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  sub,
  big,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  big?: boolean;
  accent?: boolean;
}) {
  return (
    <div>
      <div className="term-label">{label}</div>
      <div
        className="mono"
        style={{
          fontSize: big ? 26 : 20,
          fontWeight: 700,
          lineHeight: 1.15,
          marginTop: 4,
          color: accent ? "var(--accent)" : "var(--ink)",
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ── Inbox ──────────────────────────────────────────────────────────────
   Built from the send log, so it shows the EVENT rather than the email.
   A business whose address bounced still sees every job that came in,
   which is exactly when they need to. Each line names its page, because
   with two businesses "someone finished their job" is not enough. */

function Inbox({
  inbox,
  onRead,
}: {
  inbox: HomeData["inbox"];
  onRead(id?: string): void;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? inbox.items : inbox.items.slice(0, 4);

  return (
    <section className="panel px-5 py-5">
      <div className="flex items-center justify-between gap-3">
        <div className="term-label">
          What's happened
          {inbox.unread > 0 && (
            <span
              className="mono"
              style={{
                marginLeft: 8,
                background: "var(--accent)",
                color: "var(--accent-ink)",
                borderRadius: 999,
                padding: "0.1rem 0.45rem",
                fontSize: 11,
              }}
            >
              {inbox.unread} new
            </span>
          )}
        </div>
        {inbox.unread > 0 && (
          <button
            type="button"
            className="btn btn-quiet btn-term"
            style={{ padding: "0.25rem 0.6rem", fontSize: 12.5 }}
            onClick={() => onRead()}
          >
            Mark all read
          </button>
        )}
      </div>

      {inbox.items.length === 0 ? (
        <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 8, lineHeight: 1.55 }}>
          Nothing yet. When somebody finishes a chat, or asks for a person, it lands here.
        </p>
      ) : (
        <>
          <div className="mt-3 space-y-1.5">
            {shown.map((n) => (
              <div
                key={n.id}
                className="panel px-3.5 py-3 flex items-start justify-between gap-3"
                style={{ borderLeft: n.read ? undefined : "3px solid var(--accent)" }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: n.read ? 550 : 650, fontSize: 14.5 }}>
                    {n.body || n.title}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                    {n.page.displayName} · {when(n.at)}
                    {!n.delivered && (
                      <span style={{ color: "var(--danger)" }}>
                        {" · we couldn't email you this one"}
                      </span>
                    )}
                  </div>
                </div>
                {n.open && (
                  <a
                    href={n.open}
                    className="btn btn-quiet btn-term"
                    style={{ padding: "0.25rem 0.6rem", fontSize: 12.5, flexShrink: 0 }}
                    onClick={() => !n.read && onRead(n.id)}
                  >
                    Open
                  </a>
                )}
              </div>
            ))}
          </div>
          {inbox.items.length > 4 && (
            <button
              type="button"
              className="btn btn-quiet btn-term mt-2.5"
              style={{ padding: "0.3rem 0.7rem", fontSize: 13 }}
              onClick={() => setAll((a) => !a)}
            >
              {all ? "Show less" : `Show all ${inbox.items.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/* ── One chat ───────────────────────────────────────────────────────────── */

function Card({ page, theme }: { page: Page; theme: Theme }) {
  const accent = accentFor(page.branding) ?? "var(--accent)";
  return (
    <a
      href={`/dashboard/${page.slug}`}
      className="panel px-4 py-4 flex items-center gap-3.5"
      style={{ textDecoration: "none", color: "inherit", borderLeft: `3px solid ${accent}` }}
    >
      {page.branding.logoUrl ? (
        <img
          src={page.branding.logoUrl}
          alt=""
          style={{ height: 34, maxWidth: 60, width: "auto", borderRadius: 4, flexShrink: 0 }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            background: accent,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            color: theme === "dark" ? "#16160f" : "#fff",
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {page.displayName.slice(0, 1).toUpperCase()}
        </span>
      )}

      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontWeight: 650, fontSize: 15.5 }}>{page.displayName}</span>
        <span className="mono" style={{ display: "block", fontSize: 12.5, color: "var(--ink-3)" }}>
          letcraig.ai/{page.slug}
          {page.spentThisMonthMicros > 0 && ` · ${money(page.spentThisMonthMicros)} this month`}
        </span>
      </span>

      {page.waitingOnYou > 0 && (
        <span
          className="mono"
          style={{
            background: "var(--accent)",
            color: "var(--accent-ink)",
            borderRadius: 999,
            padding: "0.15rem 0.5rem",
            fontSize: 11.5,
            flexShrink: 0,
          }}
        >
          {page.waitingOnYou} waiting
        </span>
      )}
      <span
        className="mono"
        style={{
          fontSize: 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: page.published ? "var(--ok)" : "var(--ink-3)",
          flexShrink: 0,
        }}
      >
        {page.published ? "live" : "private"}
      </span>
    </a>
  );
}


/* ── The questionnaire ──────────────────────────────────────────────────
   Every question on it is a button.

   This is the quiet alternative to the interview, and the rule that
   shapes it is that it must not become a smaller interview. Nothing here
   asks a business to describe itself: no "what do you do", no "who can't
   you help", no box to write prose into. Somebody who wanted to answer
   those questions would be better served by crAIg asking them properly,
   one at a time, with follow-ups — which is exactly what /start is for.
   A form asking the same things badly is the worst of both.

   So what is left is the one question with a fixed set of answers: what
   should this chat handle? Five options, tick as many as apply, and a
   template underneath for the business that really is a tax practice or a
   trade. It is answerable in ten seconds and nothing on it can be
   answered wrongly.

   ── The two boxes at the bottom ──────────────────────────────────────
   A chat needs a name and a public address, and neither can be clicked
   into existence. They sit last, after everything is decided, and they
   are framed as what to call the thing rather than as questions about the
   business. The address writes itself from the name; the "change" button
   is there for the business whose trading name makes an ugly one.

   ── It does not ask what industry you are in ─────────────────────────
   It used to. The question was "What kind of work?" over a row of every
   pack we ship, which meant somebody making their very first chat was
   shown "Australian Individual Tax Return" and "Home Services Job Intake"
   and asked to pick one. Almost nobody is either, and being made to
   choose says, louder than any copy on the page, that those two things
   are all this does. */

function Questionnaire({
  purposes,
  templates,
  onCancel,
  onMade,
}: {
  purposes: HomeData["purposes"];
  templates: HomeData["templates"];
  onCancel(): void;
  onMade(): void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  /* Null is the ordinary case and stays the ordinary case. A template is
     the second door within the second door, and choosing one replaces the
     purposes rather than adding to them — it brings its own pack, which
     has never heard of the five. */
  const [template, setTemplate] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [editingSlug, setEditingSlug] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const address = editingSlug ? slug : suggest(name);
  const chosen = Boolean(template) || picked.length > 0;
  const ready = Boolean(name.trim()) && address.length >= 3 && chosen;

  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/dash/pages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          template
            ? { businessName: name, slug: address, templateId: template }
            : { businessName: name, slug: address, purposes: picked },
        ),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) return setError(body.error ?? "That didn't work.");
      onMade();
    } catch {
      setError("Couldn't reach us just now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="panel px-4 py-4 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy && ready) void submit();
      }}
    >
      {/*
        A group, not a row of independent buttons — a screen reader should
        hear "what should this chat handle" once and then five options,
        rather than five unrelated toggles with no idea what they belong to.
      */}
      <fieldset
        /* Disabled, not hidden, while a template is chosen. Its own pack
           decides what it asks, so these five would be a lie — but taking
           them off the screen would leave somebody wondering what they
           just gave up. Greyed out, still readable, one click back. */
        disabled={Boolean(template)}
        style={{ border: "none", padding: 0, margin: 0, opacity: template ? 0.45 : 1 }}
      >
        <legend className="term-label" style={{ padding: 0, marginBottom: 5 }}>
          What should it handle?
        </legend>
        <div className="flex flex-col gap-1.5">
          {purposes.map((p) => {
            const on = picked.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={on}
                className={on ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
                onClick={() => toggle(p.id)}
                style={{ textAlign: "left", display: "block", width: "100%" }}
              >
                <span style={{ display: "block" }}>{p.label}</span>
                {/* Inherits colour, so it stays legible once selected. */}
                <span style={{ display: "block", fontSize: 11.5, opacity: 0.72, lineHeight: 1.4 }}>
                  {p.hint}
                </span>
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 5, lineHeight: 1.45 }}>
          Pick as many as you like — most businesses want two or three. It decides what your
          customers are offered and what crAIg asks them. Changeable later.
        </p>
      </fieldset>

      {/*
        A worked starting point, and deliberately the smaller offer.

        These used to BE the question — "what kind of work?" over a row of
        every pack we ship — which told somebody making their first chat
        that a tax return and a trades job were the only two things this
        does. Offered underneath, the same two cost nothing: nobody has to
        read them, and the one business in a hundred they suit saves an hour.
      */}
      {templates.length > 0 && (
        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={{ padding: 0, marginBottom: 5, fontSize: 12, color: "var(--ink-3)" }}>
            Or start from a worked example
          </legend>
          <div className="flex flex-col gap-1.5">
            {templates.map((t) => {
              const on = template === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={on}
                  className={on ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
                  // Choosing the same one again is how you get back to the
                  // purposes without reloading the form.
                  onClick={() => setTemplate(on ? null : t.id)}
                  style={{ textAlign: "left", display: "block", width: "100%" }}
                >
                  <span style={{ display: "block" }}>{t.label}</span>
                  <span style={{ display: "block", fontSize: 11.5, opacity: 0.72, lineHeight: 1.4 }}>
                    {t.blurb}
                  </span>
                </button>
              );
            })}
          </div>
          <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 5, lineHeight: 1.45 }}>
            {template
              ? "This brings its own questions, so the list above doesn't apply. Tap it again to go back."
              : "A starting point you can change — it sets up the questions and the wording, never your prices or hours."}
          </p>
        </fieldset>
      )}

      {/* ── What to call it ────────────────────────────────────────────
          The only typing on the page, and it is last on purpose. */}
      <div className="space-y-2" style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
        <Field label="What's the business called?">
          <input
            className="field"
            maxLength={120}
            placeholder="Riverside Swim School"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        {editingSlug ? (
          <Field label="Its web address">
            <div className="flex items-center gap-1">
              <span className="mono" style={{ fontSize: 13.5, color: "var(--ink-3)" }}>
                letcraig.ai/
              </span>
              <input
                className="field mono"
                style={{ flex: 1 }}
                autoFocus
                maxLength={32}
                placeholder="riverside"
                value={address}
                onChange={(e) => setSlug(suggest(e.target.value))}
              />
            </div>
          </Field>
        ) : (
          /* Written for them, and shown so it is never a surprise. This is
             the address they will put on a van, so it is on the screen at
             the moment they make the thing rather than discovered after. */
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="mono" style={{ fontSize: 13, color: "var(--ink-3)" }}>
              letcraig.ai/{address || "…"}
            </span>
            <button
              type="button"
              onClick={() => {
                setSlug(address);
                setEditingSlug(true);
              }}
              style={{
                background: "none",
                border: 0,
                padding: 0,
                font: "inherit",
                fontSize: 12.5,
                color: "var(--ink-3)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              change
            </button>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" style={{ fontSize: 13.5, color: "var(--danger)" }}>
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="btn btn-primary btn-term" disabled={busy || !ready}>
          {busy ? "…" : "Make it"}
        </button>
        <button type="button" className="btn btn-quiet btn-term" onClick={onCancel}>
          Cancel
        </button>
        {/* The louder door, still there for anyone who opened this and
            then thought better of it. */}
        <a href="/start" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
          or let crAIg ask instead
        </a>
      </div>
    </form>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

function Verify({ email }: { email: string }) {
  const [sent, setSent] = useState(false);
  return (
    <section
      className="panel px-5 py-4"
      style={{ background: "var(--warn-tint)", borderColor: "var(--warn)" }}
    >
      <div className="term-label">One thing outstanding</div>
      <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 6, lineHeight: 1.55 }}>
        We've sent a link to <strong>{email}</strong>. Click it and you can put chats live. Until
        then everything here works, but they stay private.
      </p>
      <button
        type="button"
        className="btn btn-quiet btn-term"
        style={{ marginTop: 10 }}
        disabled={sent}
        onClick={async () => {
          await fetch("/api/account/verify/send", { method: "POST" });
          setSent(true);
        }}
      >
        {sent ? "Sent — have a look" : "Send it again"}
      </button>
    </section>
  );
}

/** Relative for the recent, plain for the rest. Nobody wants an ISO string. */
function when(at: string): string {
  const mins = Math.round((Date.now() - new Date(at).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(at).toLocaleDateString();
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="term-label" style={{ display: "block", marginBottom: 4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="flex flex-col items-center gap-3">
        <CraigFace size={40} thinking />
        <span style={{ fontSize: 14, color: "var(--ink-3)" }}>{children}</span>
        <Wordmark size={12} muted />
      </div>
    </div>
  );
}
